import { computed, Injectable, signal } from '@angular/core';

import {
  CompanyRegistrationCompany,
  CompanyRegistrationIdentity,
  RegistrationMediaData,
} from '@falcon/app/core/api/company-registration/company-registration.model';

/**
 * In-memory state shared across the self-onboarding wizard steps. Holds the data a step needs from
 * an earlier one (the registration id + VAT captured on the contact step, the identity returned by
 * itsme) without threading it through router state.
 *
 */

const STORAGE_KEY = 'falcon.company-registration.state';

interface PersistedState {
  registrationId: string | null;
  vatNumber: string | null;
  email: string | null;
  verificationToken: string | null;
  identity: CompanyRegistrationIdentity | null;
  awaitingPassword: boolean;
}

@Injectable({ providedIn: 'root' })
export class CompanyRegistrationStateService {
  readonly #registrationId = signal<string | null>(null);
  readonly #vatNumber = signal<string | null>(null);
  readonly #email = signal<string | null>(null);
  readonly #identity = signal<CompanyRegistrationIdentity | null>(null);
  readonly #verificationToken = signal<string | null>(null);
  readonly #awaitingPassword = signal(false);
  readonly #idCardFront = signal<RegistrationMediaData | null>(null);
  readonly #idCardBack = signal<RegistrationMediaData | null>(null);
  readonly #pcCode = signal<string | null>(null);
  readonly #registrationCompany = signal<CompanyRegistrationCompany | null>(null);

  readonly registrationId = this.#registrationId.asReadonly();
  readonly vatNumber = this.#vatNumber.asReadonly();
  readonly email = this.#email.asReadonly();
  readonly identity = this.#identity.asReadonly();
  readonly verificationToken = this.#verificationToken.asReadonly();
  readonly awaitingPassword = this.#awaitingPassword.asReadonly();
  readonly idCardFront = this.#idCardFront.asReadonly();
  readonly idCardBack = this.#idCardBack.asReadonly();
  readonly pcCode = this.#pcCode.asReadonly();
  readonly registrationCompany = this.#registrationCompany.asReadonly();

  /** VAT formatted for display, e.g. "BE0678662280" -> "BE0678.662.280". */
  readonly formattedVatNumber = computed(() => formatVatNumber(this.#vatNumber()));

  constructor() {
    this.#restore();
  }

  setRegistrationId(registrationId: string): void {
    this.#registrationId.set(registrationId);
    this.#persist();
  }

  setContact(registrationId: string, email: string, vatNumber: string): void {
    this.#registrationId.set(registrationId);
    this.#email.set(email);
    this.#vatNumber.set(vatNumber);
    this.#persist();
  }

  setIdentity(identity: CompanyRegistrationIdentity): void {
    this.#identity.set(identity);
    this.#persist();
  }

  setVerificationToken(verificationToken: string): void {
    this.#verificationToken.set(verificationToken);
    this.#persist();
  }

  clearVerificationToken(): void {
    this.#verificationToken.set(null);
    this.#persist();
  }

  /**
   * Opens and closes the window in which the Cognito user exists without a password. While it is
   * open the owner cannot log in and cannot re-submit the contact step, so `registrationInProgressGuard`
   * keeps them on the step they still owe. Defaults to closed: a wizard resumed after login, or one
   * whose stored state was lost, must never be treated as half-created.
   */
  setAwaitingPassword(): void {
    this.#awaitingPassword.set(true);
    this.#persist();
  }

  clearAwaitingPassword(): void {
    this.#awaitingPassword.set(false);
    this.#persist();
  }

  setIdCard(front: RegistrationMediaData, back: RegistrationMediaData): void {
    this.#idCardFront.set(front);
    this.#idCardBack.set(back);
  }

  setPcCode(pcCode: string): void {
    this.#pcCode.set(pcCode);
  }

  setCompany(company: CompanyRegistrationCompany | null): void {
    this.#registrationCompany.set(company);
  }

  reset(): void {
    this.#registrationId.set(null);
    this.#vatNumber.set(null);
    this.#email.set(null);
    this.#identity.set(null);
    this.#verificationToken.set(null);
    this.#awaitingPassword.set(false);
    this.#idCardFront.set(null);
    this.#idCardBack.set(null);
    this.#pcCode.set(null);
    this.#registrationCompany.set(null);

    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      // ignore storage errors
    }
  }

  #persist(): void {
    const state: PersistedState = {
      registrationId: this.#registrationId(),
      vatNumber: this.#vatNumber(),
      email: this.#email(),
      verificationToken: this.#verificationToken(),
      identity: this.#identity(),
      awaitingPassword: this.#awaitingPassword(),
    };

    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch {}
  }

  #restore(): void {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (!raw) return;

      const state: PersistedState = JSON.parse(raw);
      this.#registrationId.set(state.registrationId);
      this.#vatNumber.set(state.vatNumber);
      this.#email.set(state.email);
      this.#verificationToken.set(state.verificationToken ?? null);
      this.#identity.set(state.identity);
      this.#awaitingPassword.set(state.awaitingPassword ?? false);
    } catch {
      sessionStorage.removeItem(STORAGE_KEY);
    }
  }
}

export function formatVatNumber(vat: string | null | undefined): string {
  if (!vat) return '';

  // The backend returns the VAT without the country prefix ("0678662280"); the user may enter it
  // with "BE". Registration is Belgium-only, so normalise both to "BE 0678.662.280".
  const match = /^(?:BE)?\s?(\d{4})\.?(\d{3})\.?(\d{3})$/i.exec(vat.trim());

  return match ? `BE ${match[1]}.${match[2]}.${match[3]}` : vat;
}
